import { If, location, onCleanup, onMount, reactive } from 'cdui-js';
import i18n from '../../../i18n';
import { DiFiNoData } from '../../../components/DiFi/DiFiNoData';
import { showModal } from '../components/Modal';
import { bootFundRecordsApp } from '../deposit/h5-deposit/appBridge';
import arrowRight from '../../../assets/deposit-records/exchange-arrow.svg';
import { CurrencyFlag } from '../../../components/CurrencyFlag';
import { currencyName } from '../currency-exchange/shared';
import { RecordShell } from './Shell';
import {
  canCancelKind,
  canShowCancel,
  cancelConfirmText,
  cancelRecord,
  depositStatus,
  detailHeroOf,
  detailRowsOf,
  ensureMarketBanks,
  fetchRecordDetail,
  listUrl,
  loadVoucherUrl,
  proofPathsOf,
  recordDetailOf,
  recordIdKey,
  revokeAuthFileBlobUrl,
  type RecordKind,
  unwrapRecordDetail,
} from './shared';
import './deposit-records.css';

/** 详情骨架：对齐 hero + 若干 detail-row */
const RecordDetailSkeleton = () => (
  <div class="deposit-records-detail-skeleton difi-skeleton-animate" aria-busy="true">
    <div class="deposit-records-hero">
      <div class="deposit-records-skel-line deposit-records-skel-hero-label" />
      <div class="deposit-records-skel-line deposit-records-skel-hero-amount" />
    </div>
    {[0, 1, 2, 3].map(() => (
      <div class="deposit-records-detail-row">
        <div class="deposit-records-skel-line deposit-records-skel-detail-label" />
        <div class="deposit-records-skel-line deposit-records-skel-detail-value" />
      </div>
    ))}
  </div>
);

export const DepositRecordDetail = () => {
  const state = reactive({
    kind: 'deposit' as RecordKind,
    id: '',
    row: null as any,
    loading: true,
    cancelling: false,
    proofs: [] as { path: string; url: string }[],
    preview: -1,
  });

  /** 撤回成功后按当前类型进列表，保证落在对应 tab 并刷新 */
  const backToListAfterCancel = () => {
    location.routeTo(listUrl(state.kind));
  };

  onMount(() => {
    const hit = recordDetailOf();
    const kind = hit?.kind || 'deposit';
    const id = hit?.id || '';
    state.kind = kind;
    state.id = id;
    void (async () => {
      await bootFundRecordsApp();
      if (kind === 'deposit') {
        try {
          await ensureMarketBanks();
        } catch {
          // 字典失败时退回接口银行名
        }
      }
      try {
        const res = (await fetchRecordDetail(kind, id)) as any;
        state.row = unwrapRecordDetail(res, kind);
      } catch {
        state.row = null;
      }
      state.loading = false;
      if (kind !== 'deposit') return;
      const paths = proofPathsOf(state.row?.depositImagePaths);
      const loaded = await Promise.all(
        paths.map(async (path) => {
          try {
            return { path, url: await loadVoucherUrl(path) };
          } catch {
            return { path, url: '' };
          }
        }),
      );
      state.proofs = loaded.filter((item) => item.url);
    })();
  });

  onCleanup(() => {
    state.proofs.forEach((item) => revokeAuthFileBlobUrl(item.url));
  });

  const askCancel = () => {
    if (!canCancelKind(state.kind)) return;
    const id = String(state.row?.[recordIdKey(state.kind)] ?? state.id);
    if (!id || state.cancelling) return;
    showModal({
      title: i18n.FundRecords.CancelApplyTitle,
      content: () => cancelConfirmText(state.kind),
      cancelText: i18n.FundH5.No,
      confirmText: i18n.FundH5.Yes,
      onConfirm: () => {
        if (state.cancelling) return;
        state.cancelling = true;
        void cancelRecord(state.kind, id)
          .then(() => backToListAfterCancel())
          .catch(() => {
            state.cancelling = false;
          });
      },
    });
  };

  const status = () => depositStatus(state.row?.status);
  const hero = () => detailHeroOf(state.kind, state.row || {});
  const rows = () => detailRowsOf(state.kind, state.row || {}, state.id, state.proofs.length > 0);
  const queryAllow = () => (location.query as Record<string, string>)?.allowCancel;
  const canCancel = () => canShowCancel(state.kind, state.row, queryAllow());

  return (
    <RecordShell>
      <div class={`deposit-records-detail${canCancel() ? ' deposit-records-detail-foot' : ''}`}>
        {state.loading ? <RecordDetailSkeleton /> : null}
        {!state.loading && !state.row ? (
          <DiFiNoData hasIcon text={i18n.FundRecords.Empty} style={{ padding: '80px 0' }} />
        ) : null}
        {state.row ? (
          <div>
            {state.kind === 'exchange' ? (
              <div class="deposit-records-exchange-pair">
                <div class="deposit-records-exchange-side">
                  <CurrencyFlag code={state.row.fromCurrency} class="deposit-records-exchange-flag" />
                  <span>{currencyName(state.row.fromCurrency)}</span>
                </div>
                <img class="deposit-records-exchange-arrow" src={arrowRight} alt="" />
                <div class="deposit-records-exchange-side">
                  <CurrencyFlag code={state.row.toCurrency} class="deposit-records-exchange-flag" />
                  <span>{currencyName(state.row.toCurrency)}</span>
                </div>
              </div>
            ) : (
              <div class="deposit-records-hero">
                <div class="deposit-records-hero-label">{hero().label}</div>
                <div class="deposit-records-hero-amount">{hero().amount}</div>
              </div>
            )}
            {rows().map((item) => (
              <div class={`deposit-records-detail-row${item.amount ? ' deposit-records-detail-row-amount' : ''}`}>
                <div class="deposit-records-detail-label">{item.label}</div>
                {item.proofs ? (
                  <div class="deposit-records-proofs">
                    {state.proofs.map((proof, index) => (
                      <button type="button" class="deposit-records-proof" onclick={() => (state.preview = index)}>
                        <img src={proof.url} alt={i18n.FundRecords.ProofAlt} />
                      </button>
                    ))}
                  </div>
                ) : item.amount ? (
                  <div class="deposit-records-detail-amount">
                    <span class="deposit-records-detail-amount-num">{item.amount.text}</span>
                    <span class="deposit-records-detail-amount-code">{item.amount.currency}</span>
                  </div>
                ) : item.status && status().tone !== 'muted' ? (
                  <div
                    class={`deposit-records-status-tag deposit-records-status-tag-${status().tone}`}
                  >
                    {item.value}
                  </div>
                ) : (
                  <div
                    class={`deposit-records-detail-value${item.status ? ` deposit-records-status-${status().tone}` : ''}`}
                  >
                    {item.value}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : null}
      </div>
      <If when={canCancel()}>
        <div class="deposit-records-footer">
          <button type="button" class="deposit-records-foot-cancel" disabled={state.cancelling} onclick={askCancel}>
            {i18n.FundRecords.Revoke}
          </button>
        </div>
      </If>
      {state.preview >= 0 && state.proofs[state.preview] ? (
        <div class="deposit-records-preview" role="dialog" aria-label={i18n.FundRecords.PreviewAria}>
          <div class="deposit-records-preview-bar">
            <span>{i18n.FundRecords.PreviewAria}</span>
            <span>
              {state.preview + 1} / {state.proofs.length}
            </span>
            <button type="button" onclick={() => (state.preview = -1)}>
              {i18n.FundRecords.ClosePreview}
            </button>
          </div>
          <div class="deposit-records-preview-stage">
            {state.proofs.length > 1 ? (
              <button
                type="button"
                class="deposit-records-preview-nav"
                onclick={() => {
                  state.preview = (state.preview - 1 + state.proofs.length) % state.proofs.length;
                }}
              >
                <svg class="deposit-records-preview-nav-icon" viewBox="0 0 16 16" aria-hidden="true">
                  <path
                    d="M10 3.5L5.5 8L10 12.5"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.75"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            ) : null}
            <img src={state.proofs[state.preview].url} alt="" />
            {state.proofs.length > 1 ? (
              <button
                type="button"
                class="deposit-records-preview-nav"
                onclick={() => {
                  state.preview = (state.preview + 1) % state.proofs.length;
                }}
              >
                <svg class="deposit-records-preview-nav-icon" viewBox="0 0 16 16" aria-hidden="true">
                  <path
                    d="M6 3.5L10.5 8L6 12.5"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.75"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </RecordShell>
  );
};
